import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getMe, type MeDto } from '../lib/entities/me';

/**
 * The signed-in user, loaded once for the app shell.
 *
 * Used for the greeting ("Good morning, {firstName}") and anywhere else the UI
 * needs the human behind the token. Deliberately resilient: a failed fetch just
 * yields null so callers fall back to a generic greeting rather than break.
 */
interface CurrentUserValue {
  user: MeDto | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const CurrentUserContext = createContext<CurrentUserValue | null>(null);

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setUser(await getMe());
    } catch {
      // Quiet failure: the greeting falls back to a generic form.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<CurrentUserValue>(
    () => ({ user, loading, refetch }),
    [user, loading, refetch],
  );

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser(): CurrentUserValue {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser must be used inside CurrentUserProvider');
  return ctx;
}
